// The melody GATE (plan §4, §6): a candidate chord is only offered when
// every pitch class the melody sounds over it is a chord tone or an admitted
// tension of that chord. A hard filter, not a penalty — a wrong note over a
// clever chord is a mistake, not a reharm. Chord-only charts have no gate.
//
// One deliberate softening, documented: a PASSING tone — a pitch class
// sounding under half a beat in total and not on the chord's onset — is
// ignored. Real melodies run chromatic approach notes through every chord,
// and a gate that read each one as a chord tone would refuse the tritone
// sub on half the corpus while teaching nothing.
//
// Two dominant-specific rules from Felts (Reharmonization Techniques, the
// tension-availability chapter), derived rather than copied:
//   1. A melody ♭9 over a DOMINANT is available only when that dominant
//      resolves down a perfect fifth. Over a dominant going anywhere else it
//      is a wrong note, so the gate refuses it (a tritone sub carrying the
//      melody as its ♭9 is exactly the case — lydian ♭7 has no ♭9).
//   2. An ALTERED tension (♭9 ♯9 ♯11 ♭13) wants to resolve DOWN. A melody
//      that climbs off one is not wrong, but it reads unresolved, so it is a
//      soft count (`unresolvedAltered`) the ranking uses after function.

import { CHORD_TYPES, type ChordTypeId } from '../chords';
import { pitchClassOf } from '../notes';
import { admittedTensions } from '../tensions';
import type { SungPitchClass } from './context';
import type { MelodyFit } from './types';

const PASSING_TONE_BEATS = 0.5;
const FLAT_NINE = 1;
const ALTERED = new Set([1, 3, 6, 8]);

export interface MelodyFitOptions {
  /** A slash bass sounds, so it is a chord tone. */
  bass?: string;
  /** The candidate dominant resolves down a fifth to what follows it. Only
   *  read for dominant-family chords; undefined = not known to resolve. */
  dominantResolves?: boolean;
}

const mod12 = (n: number) => ((n % 12) + 12) % 12;

function isDominantFamily(chordType: ChordTypeId): boolean {
  return CHORD_TYPES[chordType].category === 'dominant';
}

/** The melody notes the gate weighs: everything but passing tones. */
function weighed(melody: readonly SungPitchClass[]): SungPitchClass[] {
  return melody.filter((note) => note.onOnset || note.beats >= PASSING_TONE_BEATS);
}

export function melodyFitFor(
  root: string,
  chordType: ChordTypeId,
  melody: readonly SungPitchClass[],
  hasMelody: boolean,
  options: MelodyFitOptions = {},
): MelodyFit {
  if (!hasMelody) return 'no-melody';
  const rootPc = pitchClassOf(root);
  const tones = new Set([0, ...CHORD_TYPES[chordType].intervals].map((iv) => mod12(rootPc + iv)));
  // A slash bass sounds, so it is a chord tone — without this a hybrid would
  // be refused for a melody note the bass is playing underneath it.
  if (options.bass) tones.add(pitchClassOf(options.bass));
  const tensions = new Set(admittedTensions(chordType).map((t) => mod12(rootPc + t)));
  const dominant = isDominantFamily(chordType);
  let fit: MelodyFit = 'chord-tone';
  for (const note of weighed(melody)) {
    // Felts rule 1: the ♭9 in the melody needs the dominant to resolve.
    if (dominant && mod12(note.pc - rootPc) === FLAT_NINE && !options.dominantResolves) {
      return 'none';
    }
    if (tones.has(note.pc)) continue;
    if (tensions.has(note.pc)) {
      fit = 'tension';
      continue;
    }
    return 'none';
  }
  return fit;
}

/** Felts rule 2: how many altered tensions the melody sounds over this
 *  dominant and then leaves upward. Zero for anything but a dominant. */
export function unresolvedAlteredFor(
  root: string,
  chordType: ChordTypeId,
  melody: readonly SungPitchClass[],
): number {
  if (!isDominantFamily(chordType)) return 0;
  const rootPc = pitchClassOf(root);
  let count = 0;
  for (const note of weighed(melody)) {
    if (ALTERED.has(mod12(note.pc - rootPc)) && note.risesAfter) count++;
  }
  return count;
}

/** Worst of two fits, for a candidate made of several chords. */
export function worseFit(a: MelodyFit, b: MelodyFit): MelodyFit {
  const rank: Record<MelodyFit, number> = { 'chord-tone': 0, tension: 1, 'no-melody': 2, none: 3 };
  return rank[a] >= rank[b] ? a : b;
}
